import React from "react";
import WaveformPlayer from "./waveformPlayer";
import CircularProgressRing from "./CircularProgressRing";

const StageIcon = ({ stroke = "#cbd5e1" }) => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.71-.71a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const FIT_LABEL_META = {
  EXCELLENT_FIT: { text: "Excellent Fit", badgeClass: "excellent" },
  GOOD_FIT: { text: "Good Fit", badgeClass: "average" },
  NOT_FIT: { text: "Not a Fit", badgeClass: "weak" },
};

const VoiceInterviewStage = ({
  candidate,
  voiceSession,
  currentStage,
  currentStatus,
  tabLoading,
  isSharedReport,
  handleVoiceCall,
  handleVoiceRefresh,
  formatDateTime,
  loadingCall,
  refreshing,
  audioLoading,
  setAudioLoading,
  expandSummary,
  setExpandSummary,
  expandRecommendation,
  setExpandRecommendation,
  expandFitRationale,
  setExpandFitRationale,
  handleScoreClick,
  setShowVideoModal,
  setModalType,
  setShowPhoneCheckModal,
  initiatingVideoCall,
  onScheduleVoice,
  hideVoiceCallActions,
}) => {
  const isMobile = window.innerWidth < 767;

  if (tabLoading) {
    return (
      <div className="cpv2-loading" style={{ minHeight: 200 }}>
        <div className="spinner-border" />
      </div>
    );
  }

  const sessionStatus = (voiceSession?.status || "").toLowerCase();
  const isCompleted =
    (sessionStatus === "ended" || sessionStatus === "completed") && voiceSession?.result;

  // call_source reflects how the call was placed, independent of whether analysis has finished processing.
  const isDynamicVoiceQA = voiceSession?.call_source === "DYNAMIC_VOICE_CALL";
  const qaPairs = voiceSession?.result?.analysis?.qa_pairs || [];
  const fitLabel = voiceSession?.result?.analysis?.fit_label;
  const fitRationale = voiceSession?.result?.analysis?.fit_rationale;

  const callBtn = (label, onClick, disabled) =>
    !isSharedReport && !hideVoiceCallActions && (
      <button className="cpv2-voice__btn cpv2-voice__btn--primary" onClick={onClick} disabled={disabled}>
        {label}
      </button>
    );

  const scheduleBtn = !isSharedReport && !hideVoiceCallActions && (
    <button
      className="cpv2-voice__btn"
      disabled={initiatingVideoCall}
      onClick={() => {
        const hasCountryCode = /^\+\d{1,3}/.test(candidate?.phone_number || "");
        if (hasCountryCode) { onScheduleVoice(); } else { setShowPhoneCheckModal(true); }
      }}
    >
      Schedule a voice interview
    </button>
  );

  const voiceCallBtn = (label) =>
    !isSharedReport && !hideVoiceCallActions && (
      <button
        className="cpv2-voice__btn cpv2-voice__btn--primary"
        disabled={loadingCall}
        onClick={() => {
          const hasCountryCode = /^\+\d{1,3}/.test(candidate?.phone_number || "");
          if (hasCountryCode) { handleVoiceCall(); } else { setShowPhoneCheckModal(true); }
        }}
      >
        {loadingCall ? "Initiating call..." : label}
      </button>
    );

  const TranscriptMessages = () => (
    <>
      {voiceSession?.result?.messages?.map((msg, idx) => {
        // Standard VAPI calls use `message`/lowercase roles ("bot", "tool_calls");
        // dynamic voice assistant calls use `content`/different-cased roles ("AI", "User").
        const role = (msg.role || "").toLowerCase();
        const text = msg.content ?? msg.message;
        if (role === "system" || !text?.trim()) return null;
        const isAgent = ["bot", "tool_calls", "tool_call_result", "ai", "assistant", "agent"].includes(role);
        const speaker = isAgent ? "Agent voice" : candidate?.name;
        return (
          <div className="cpv2-video__transcript-row" key={idx}>
            {isAgent ? (
              <div className="agent-avatar">
                <img src="/images/candidate-example.jpeg" alt="avatar" />
              </div>
            ) : (
              <div className="avatar">
                {candidate.name?.charAt(0).toUpperCase() || "U"}
              </div>
            )}
            <div>
              <p className="speaker">{speaker}</p>
              <p className="msg">{text}</p>
            </div>
          </div>
        );
      })}
    </>
  );

  if (isCompleted) {
    const analysis = voiceSession?.result?.analysis?.structured_analysis;
    const completedDate = voiceSession?.completed_at || voiceSession?.updated_at;
    // Dynamic voice assistant calls only populate `audio_url`, not `stereo_audio_url`.
    const resolvedAudioUrl = voiceSession?.result?.stereo_audio_url || voiceSession?.result?.audio_url;

    return (
      <div className="cpv2-voice">
        {/* Score boxes */}
        <div className="cpv2-voice__scores">
          {!isDynamicVoiceQA && (
            <CircularProgressRing
              percentage={(voiceSession?.result?.overall_score || 0).toFixed(2)}
              label="Voice interview"
            />
          )}
          {analysis && (
            <>
              <div className="cpv2-voice__score-box">
                <h6>{parseFloat(analysis.communication_weighted_score || 0).toFixed(2)}/{analysis.weights_used?.communication || "25"}</h6>
                <p>Communication Weighted Score</p>
              </div>
              <div className="cpv2-voice__score-box">
                <h6>{parseFloat(analysis.technical_weighted_score || 0).toFixed(2)}/{analysis.weights_used?.technical || "60"}</h6>
                <p>Technical Weighted Score</p>
                {analysis.technical_evaluation?.length > 0 && (
                  <button onClick={() => handleScoreClick("technical")}>View</button>
                )}
              </div>
              <div className="cpv2-voice__score-box">
                <h6>{parseFloat(analysis.cultural_fit_weighted_score || 0).toFixed(2)}/{analysis.weights_used?.cultural_fit || "15"}</h6>
                <p>Cultural Fit Weighted Score</p>
                {analysis.cultural_fit_evaluation?.length > 0 && (
                  <button onClick={() => handleScoreClick("cultural")}>View</button>
                )}
              </div>
              <div className="cpv2-voice__score-box">
                <h6>{parseFloat(analysis.final_weighted_score || 0).toFixed(2)}/100</h6>
                <p>Final Weighted Score</p>
                {([...(analysis.technical_evaluation || []), ...(analysis.cultural_fit_evaluation || [])].length > 0) && (
                  <button onClick={() => handleScoreClick("final")}>View</button>
                )}
              </div>
            </>
          )}
          {completedDate && isDynamicVoiceQA && (
            <div className="cpv2-voice__score-box">
              <div className='d-flex align-items-center justify-content-start gap-1'>
                <div style={{borderRight: "1px solid #e5e7eb", marginRight: 10, paddingRight: 10}}>
                  <h6 style={{ fontSize: 16 }}>
                    {new Date(completedDate)
                      .toLocaleString(undefined, { day: "numeric", month: "long", year: "numeric" })
                      .replace(",", "")}
                  </h6>
                  <p>Completed</p>
                </div>
                <div>
                  <h6 style={{ fontSize: 16 }}>Type: Dynamic Voice Assistant</h6>
                  {fitLabel && FIT_LABEL_META[fitLabel] && (
                    <span className={`cpv2-voice__score-badge cpv2-voice__score-badge--${FIT_LABEL_META[fitLabel].badgeClass}`}>
                      {FIT_LABEL_META[fitLabel].text}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fit rationale (dynamic voice assistant calls) */}
        {isDynamicVoiceQA && fitLabel && fitRationale && (
          <div className="cpv2-voice__fit-rationale">
            <p className="cpv2-voice__fit-rationale-title">Fit Description</p>
            <p className={`cpv2-voice__text ${expandFitRationale ? "" : "clamped"}`}>{fitRationale}</p>
            <span className="cpv2-voice__read-more" onClick={() => setExpandFitRationale(!expandFitRationale)}>
              {expandFitRationale ? "View less" : "View more"}
            </span>
          </div>
        )}

        {/* Summary + Recommendation (standard scored calls) OR Screening Q&A (dynamic voice assistant calls) */}
        {isDynamicVoiceQA ? (
          <div className="cpv2-voice__qa-section">
            <p className="cpv2-voice__section-title">Screening Q&amp;A</p>
            {qaPairs.length > 0 ? (
              <div className="cpv2-voice__qa-list">
                {qaPairs.map((qa, idx) => (
                  <div className="cpv2-voice__qa-item" key={idx}>
                    <p className="cpv2-voice__qa-question">{qa.question}</p>
                    <p className="cpv2-voice__qa-answer">{qa.candidate_answer}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="cpv2-voice__text">No screening answers were recorded for this call.</p>
            )}
          </div>
        ) : (
          <div className="cpv2-voice__two-col">
            <div>
              <p className="cpv2-voice__section-title">Summary</p>
              <p className={`cpv2-voice__text ${expandSummary ? "" : "clamped"}`}>
                {voiceSession?.result?.summary}
              </p>
              <span
                className="cpv2-voice__read-more"
                onClick={() => setExpandSummary(!expandSummary)}
              >
                {expandSummary ? "View less" : "View more"}
              </span>
            </div>
            {voiceSession?.result?.recommendation && (
              <div>
                <p className="cpv2-voice__section-title">Recommendation</p>
                <p className={`cpv2-voice__text ${expandRecommendation ? "" : "clamped"}`}>
                  {voiceSession.result.recommendation}
                </p>
                <span
                  className="cpv2-voice__read-more"
                  onClick={() => setExpandRecommendation(!expandRecommendation)}
                >
                  {expandRecommendation ? "View less" : "View more"}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Audio + Transcript */}
        {!isMobile && (
          <>
            <p className="cpv2-voice__section-title">Interview Audio</p>
            {audioLoading && (
              <div className="cpv2-loading" style={{ minHeight: 60, flex: "none" }}>
                <div className="spinner-border" />
              </div>
            )}
            <div style={{ display: audioLoading ? "none" : "block", marginBottom: 20 }}>
              <WaveformPlayer
                audioUrl={resolvedAudioUrl}
                onLoadingChange={setAudioLoading}
                hideDownload={isSharedReport}
              />
            </div>

            <p className="cpv2-voice__section-title">Transcript</p>
            <div className="cpv2-video__transcript">
              <TranscriptMessages />
            </div>
          </>
        )}

        {/* Mobile accordion */}
        {isMobile && (
          <div className="accordion" id="voiceAccordion">
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#voiceAudio">
                  Audio and Transcript
                </button>
              </h2>
              <div id="voiceAudio" className="accordion-collapse collapse" data-bs-parent="#voiceAccordion">
                <div className="accordion-body" style={{ padding: 0 }}>
                  {audioLoading && (
                    <div className="cpv2-loading" style={{ minHeight: 60, flex: "none" }}>
                      <div className="spinner-border" />
                    </div>
                  )}
                  <div style={{ display: audioLoading ? "none" : "block" }}>
                    <WaveformPlayer
                      audioUrl={resolvedAudioUrl}
                      onLoadingChange={setAudioLoading}
                      hideDownload={isSharedReport}
                    />
                  </div>
                  <div className="cpv2-video__transcript" style={{ padding: "0 16px" }}>
                    <TranscriptMessages />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Non-completed states
  if (!currentStage) {
    return (
      <div className="cpv2-voice">
        <div className="cpv2-stage-empty">
          <StageIcon />
          <p className="cpv2-stage-empty__msg">No voice interview scheduled yet</p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            {voiceCallBtn("Make a voice call")}
            {scheduleBtn}
          </div>
        </div>
      </div>
    );
  }

  if (currentStatus === "ASSIGNED" || currentStatus === "NOT SCHEDULED") {
    return (
      <div className="cpv2-voice">
        <p className="cpv2-voice__status-msg">Voice Call assigned, yet to be scheduled</p>
      </div>
    );
  }

  if (currentStatus === "SCHEDULED") {
    return (
      <div className="cpv2-voice">
        <p className="cpv2-voice__status-msg">
          Voice Call scheduled at {formatDateTime(currentStage.scheduled_at)}
        </p>
      </div>
    );
  }

  if (currentStatus === "ONGOING") {
    return (
      <div className="cpv2-voice">
        <div className="cpv2-voice__action-row" style={{ alignItems: "center" }}>
          <p className="cpv2-voice__status-msg" style={{ margin: 0 }}>Voice Call is in progress</p>
          <button className="cpv2-voice__btn" onClick={handleVoiceRefresh} disabled={refreshing}>
            <img src="/images/reset.svg" alt="" width={16} height={16} />
          </button>
        </div>
      </div>
    );
  }

  if (currentStatus === "EXPIRED") {
    return (
      <div className="cpv2-voice">
        <div className="cpv2-stage-empty">
          <StageIcon stroke="#fcd34d" />
          <p className="cpv2-stage-empty__msg cpv2-stage-empty__msg--warning">Voice call stage has expired</p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            {voiceCallBtn("Make a voice call again")}
            {scheduleBtn}
          </div>
        </div>
      </div>
    );
  }

  if (["FAILED", "NOT COMPLETED", "REVOKED"].includes(currentStatus)) {
    return (
      <div className="cpv2-voice">
        <div className="cpv2-stage-empty">
          <StageIcon stroke="#fca5a5" />
          <p className="cpv2-stage-empty__msg cpv2-stage-empty__msg--error">
            Voice call failed
            {voiceSession?.meta_data?.failure_reason && ` — ${voiceSession.meta_data.failure_reason}`}
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            {voiceCallBtn("Make a voice call again")}
            {scheduleBtn}
          </div>
        </div>
      </div>
    );
  }

  if (
    (currentStatus === "COMPLETED" && voiceSession?.result === "null") ||
    (currentStatus === "COMPLETED" && voiceSession?.result === null)
  ) {
    return (
      <div className="cpv2-voice">
        <p className="cpv2-voice__status-msg">
          Voice Call is Completed waiting for result to get updated
        </p>
      </div>
    );
  }

  return (
    <div className="cpv2-voice">
      <p className="cpv2-voice__status-msg">Unknown or pending state</p>
    </div>
  );
};

export default VoiceInterviewStage;
